// Formül ifade alanı: çok satırlı (kendiliğinden büyüyen) monospace textarea + yazarken kod
// önerisi listesi. Tarayıcı datalist'i yerine imleçteki JETON için öneri üretir
// (`tokenAtCursor` / `suggestCodes`); ↑↓ gezinir, Enter/Tab kabul eder, Esc kapatır.
// Enter hiçbir zaman satır sonu eklemez (formül tek mantıksal satırdır; hata işaretçisi buna
// güvenir). İmleç konumu dışarıya raporlanır (CEVIR parçası imlece eklenir). `TOPLA_ALT("…` /
// `ORT_ALT("…` tırnağı içinde kod yerine boyut (grup argümanı) önerilir.

import { useId, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { KPI_BREAKDOWN_DIMENSION_LABELS, KPI_BREAKDOWN_DIMENSIONS } from "@mahir/shared";
import {
  applySuggestion,
  dimensionArgAtCursor,
  suggestCodes,
  suggestDimensions,
  tokenAtCursor,
  type CodeSuggestion,
} from "../../lib/kpi-formula-autocomplete";
import { inputStyle, monoStyle } from "./kpi-form-styles";

export interface FormulaExpressionFieldProps {
  value: string;
  ariaLabel: string;
  codes: ReadonlySet<string>;
  labelsByCode?: ReadonlyMap<string, string> | undefined;
  unitsByCode?: ReadonlyMap<string, string | null> | undefined;
  ownCode?: string | undefined;
  disabled?: boolean | undefined;
  invalid?: boolean | undefined;
  placeholder?: string | undefined;
  /** başlangıç satır sayısı (1–8 arasında büyür) */
  rows?: number | undefined;
  onChange: (value: string) => void;
  /** imleç her değiştiğinde (CEVIR/kod ekleme için) */
  onCursor?: ((cursor: number) => void) | undefined;
  /** textarea referansı — dışarıdan odak/imleç yönetimi */
  inputRef?: ((el: HTMLTextAreaElement | null) => void) | undefined;
}

const MAX_ROWS = 8;

export function FormulaExpressionField(props: FormulaExpressionFieldProps): JSX.Element {
  const {
    value,
    ariaLabel,
    codes,
    labelsByCode,
    unitsByCode,
    ownCode,
    disabled,
    invalid,
    placeholder,
    rows = 2,
    onChange,
    onCursor,
    inputRef,
  } = props;
  const listId = useId();
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const [items, setItems] = useState<CodeSuggestion[]>([]);
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const pendingCursor = useRef<number | null>(null);

  // Kendiliğinden büyüme: içerik yüksekliğine göre, en çok MAX_ROWS satır
  useLayoutEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    const line = 22;
    const max = line * MAX_ROWS + 14;
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
    el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
  }, [value]);

  // Öneri kabulünden sonra imleci eklenenin sonuna taşı
  useLayoutEffect(() => {
    const el = taRef.current;
    const c = pendingCursor.current;
    if (!el || c === null) return;
    pendingCursor.current = null;
    el.focus();
    el.setSelectionRange(c, c);
    onCursor?.(c);
  }, [value, onCursor]);

  function refresh(el: HTMLTextAreaElement): void {
    const cursor = el.selectionStart ?? el.value.length;
    onCursor?.(cursor);
    const dim = disabled ? null : dimensionArgAtCursor(el.value, cursor);
    if (dim) {
      const dims = suggestDimensions(
        dim.word,
        KPI_BREAKDOWN_DIMENSIONS,
        KPI_BREAKDOWN_DIMENSION_LABELS,
      ).filter((s) => s.insert !== dim.word);
      setItems(dims);
      setActive(0);
      setOpen(dims.length > 0);
      return;
    }
    const tok = tokenAtCursor(el.value, cursor);
    if (!tok || disabled) {
      setOpen(false);
      setItems([]);
      return;
    }
    const next = suggestCodes(tok.word, codes, {
      labelsByCode,
      unitsByCode,
      exclude: ownCode,
    }).filter((s) => s.insert !== tok.word);
    setItems(next);
    setActive(0);
    setOpen(next.length > 0);
  }

  function accept(s: CodeSuggestion): void {
    const el = taRef.current;
    if (!el) return;
    const cursor = el.selectionStart ?? el.value.length;
    const tok =
      s.kind === "dimension"
        ? dimensionArgAtCursor(el.value, cursor)
        : tokenAtCursor(el.value, cursor);
    if (!tok) return;
    const r = applySuggestion(el.value, tok, s.insert);
    pendingCursor.current = r.cursor;
    setOpen(false);
    setItems([]);
    onChange(r.expression);
  }

  const activeId = open && items[active] ? `${listId}-${active}` : undefined;

  return (
    <div style={{ position: "relative", minWidth: 0, flex: 1 }}>
      <textarea
        ref={(el) => {
          taRef.current = el;
          inputRef?.(el);
        }}
        aria-label={ariaLabel}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        {...(activeId ? { "aria-activedescendant": activeId } : {})}
        rows={rows}
        spellCheck={false}
        autoComplete="off"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        style={{
          ...inputStyle,
          ...monoStyle,
          width: "100%",
          minHeight: 36,
          lineHeight: "22px",
          padding: "6px 8px",
          resize: "none",
          display: "block",
          ...(invalid ? { borderColor: "var(--color-error)" } : {}),
        }}
        onChange={(e) => {
          onChange(e.target.value);
          refresh(e.currentTarget);
        }}
        onKeyDown={(e) => {
          if (open && items.length > 0) {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActive((a) => (a + 1) % items.length);
              return;
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((a) => (a - 1 + items.length) % items.length);
              return;
            }
            if (e.key === "Enter" || e.key === "Tab") {
              e.preventDefault();
              accept(items[active]!);
              return;
            }
            if (e.key === "Escape") {
              // yalnız listeyi kapat — modal'ın document düzeyindeki Esc dinleyicisine ulaşmasın
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              setOpen(false);
              return;
            }
          }
          if (e.key === "Enter") e.preventDefault(); // satır sonu yok
        }}
        onKeyUp={(e) => {
          if (["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"].includes(e.key)) return;
          if (e.key.startsWith("Arrow") || e.key === "Home" || e.key === "End")
            refresh(e.currentTarget);
        }}
        onClick={(e) => refresh(e.currentTarget)}
        onSelect={(e) => onCursor?.(e.currentTarget.selectionStart ?? 0)}
        onBlur={(e) => {
          onCursor?.(e.currentTarget.selectionStart ?? 0);
          // tıklama ile seçim için küçük gecikme
          window.setTimeout(() => setOpen(false), 150);
        }}
      />
      {open && items.length > 0 ? (
        <ul
          id={listId}
          role="listbox"
          aria-label="Kod önerileri"
          style={listStyle}
          onMouseDown={(e) => e.preventDefault()}
        >
          {items.map((s, i) => (
            <li
              key={`${s.kind}-${s.code}`}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onClick={() => accept(s)}
              style={{
                ...itemStyle,
                background:
                  i === active
                    ? "color-mix(in srgb, var(--color-primary) 10%, var(--color-surface))"
                    : "transparent",
              }}
            >
              <span style={{ ...monoStyle, fontWeight: 600, color: "var(--color-text)" }}>
                {s.kind === "function" ? `${s.code}(…)` : s.code}
              </span>
              {s.label ? (
                <span style={{ color: "var(--color-text-2)", minWidth: 0, flex: 1 }}>
                  {s.label}
                </span>
              ) : s.kind === "function" ? (
                <span style={{ color: "var(--color-text-3)" }}>fonksiyon</span>
              ) : null}
              {s.unit ? <span style={{ color: "var(--color-text-3)" }}>{s.unit}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

const listStyle: CSSProperties = {
  position: "absolute",
  left: 0,
  right: 0,
  top: "100%",
  marginTop: 2,
  zIndex: 30,
  listStyle: "none",
  margin: 0,
  padding: 4,
  background: "var(--color-surface)",
  border: "1px solid var(--color-border)",
  borderRadius: 8,
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  maxHeight: 260,
  overflowY: "auto",
};

const itemStyle: CSSProperties = {
  display: "flex",
  gap: 10,
  alignItems: "baseline",
  padding: "6px 8px",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: "0.82rem",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
